import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { addPrimitiveCases } from "../../../redux/shared/add-primitive-cases.ts"
import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list.ts"
import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import type { RehydrateAction } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { teamMembersSliceName } from "./team-members.constants.ts"
import {
  createTeamMember,
  deleteTeamMember,
  fetchInviteTeamMemberInfo,
  fetchTeamMember,
  fetchTeamMembers,
  fetchTeamMembersFilterOptions,
  performActionTeamMember,
  updateTeamMember
} from "./team-members.thunks.ts"
import type {
  TeamMembersFilters,
  TeamMembersState
} from "./team-members.types.ts"

// Initial State
const initialState: TeamMembersState = {
  fetching: false,
  requestId: null,
  result: {
    count: 0,
    next: null,
    results: []
  },

  filters: {
    page: 1,
    q: null,
    page_size: SCROLL_PAGE_SIZE,
    role: null,
    status: null,
    ordering: null,
    labels: null,
    team_ids: null
  },

  fetchingFilterOptions: false,
  filterOptions: {
    role: [],
    status: [],
    lead_cost_method: [],
    specialization: [],
    location: [],
    date_format: [],
    time_format: [],
    labels: [],
    project_manager: [],
    sales_manager: []
  },

  saveFetching: false,
  saveResult: null,

  userDetailsFetching: false,
  userDetails: null,

  inviteInfoFetching: false,
  inviteInfo: null
}

// Slice
const teamMembersSlice = createSlice({
  name: teamMembersSliceName,
  initialState,
  reducers: {
    usersSetProp: generateSetPropReducer<TeamMembersState>(),
    changeUsersFilters: (
      state,
      action: PayloadAction<Partial<TeamMembersFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearUsersFilters: (state) => {
      state.filters = initialState.filters
    },
    clearInviteUserInfo: (state) => {
      state.inviteInfo = null
    }
  },
  extraReducers: (builder) => {
    addPrimitiveCases(builder, createTeamMember, "saveFetching")
    addPrimitiveCases(builder, updateTeamMember, "saveFetching")
    addPrimitiveCases(builder, performActionTeamMember, "saveFetching")

    builder
      .addCase(REHYDRATE, (state, action: RehydrateAction) => {
        const teamMembersSlice = action?.payload?.[teamMembersSliceName]
        if (!teamMembersSlice) return state

        // Layered over initialState: only the persisted filters come back, and
        // a filters-only payload cannot hollow out the slice.
        return {
          ...initialState,
          filters: { ...initialState.filters, ...teamMembersSlice.filters }
        }
      })

      // fetchUsersFilterOptions
      .addCase(fetchTeamMembersFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(fetchTeamMembersFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(fetchTeamMembersFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      // fetchUsers
      .addCase(fetchTeamMembers.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
        state.result = beforeFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFetch(
          state.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFailedFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })

      // deleteUser
      .addCase(deleteTeamMember.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteTeamMember.fulfilled, (state, action) => {
        const newResults = state.result.results.filter(
          (user) => user.id !== action.payload?.id
        )

        state.saveFetching = false

        state.result = {
          ...state.result,
          count: state.result.count - 1,
          results: newResults
        }
      })
      .addCase(deleteTeamMember.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchUserDetails
      .addCase(fetchTeamMember.pending, (state) => {
        state.userDetailsFetching = true
      })
      .addCase(fetchTeamMember.fulfilled, (state, action) => {
        state.userDetailsFetching = false
        state.userDetails = action.payload
      })
      .addCase(fetchTeamMember.rejected, (state) => {
        state.userDetailsFetching = false
      })

      // fetchInviteUserInfo
      .addCase(fetchInviteTeamMemberInfo.pending, (state) => {
        state.inviteInfoFetching = true
      })
      .addCase(fetchInviteTeamMemberInfo.fulfilled, (state, action) => {
        state.inviteInfoFetching = false
        state.inviteInfo = action.payload
      })
      .addCase(fetchInviteTeamMemberInfo.rejected, (state) => {
        state.inviteInfoFetching = false
      })

      .addCase(logout.fulfilled, () => {
        console.log("users logout")
        return initialState
      })
  }
})

export const {
  usersSetProp,
  changeUsersFilters,
  clearUsersFilters,
  clearInviteUserInfo
} = teamMembersSlice.actions

export default teamMembersSlice
